import { SortDirection } from '@warebee/frontend/data-access-api-graphql';
import _ from 'lodash';
import { atom, selector } from 'recoil';
import { persistAtom } from '../../common/recoil/persistAtom';
import { AsyncLoadStatus, DataTableState } from '../../common/types';
import { actualityJobGroupFields } from './actuality.default';
import { actualityAggregatedStatsByGroups } from './actuality.kpi.state';
import {
  ActualityJob,
  ActualityJobField,
  ActualityJobs,
} from './actuality.types';
import { ActualityJobTimingMode } from './feed.default';
import { feedQueryBuilderIsReady } from './feed.state';

const getKey = (postfix: string) => `warebee-actuality-jobs-${postfix}`;

export const actualityJobs = selector<ActualityJobs>({
  key: getKey('jobs'),
  get: async ({ get }) => {
    if (!get(feedQueryBuilderIsReady)) return null;
    return get(actualityAggregatedStatsByGroups(actualityJobGroupFields));
  },
});

export const actualityJobsByAgent = selector<Record<string, ActualityJobs>>({
  key: getKey('by-agent'),
  get: async ({ get }) => {
    const all = get(actualityJobs);
    const grouped = _.groupBy(all, p => p.agentId);
    return grouped;
  },
});

export const actualityJobsByAgentType = selector<Record<string, ActualityJobs>>(
  {
    key: getKey('by-agent-type'),
    get: async ({ get }) => {
      const all = get(actualityJobs);
      const grouped = _.groupBy(all, p => p.agentType);
      return grouped;
    },
  },
);

export const actualityJobsById = selector<Record<string, ActualityJob>>({
  key: getKey('by-id'),
  get: async ({ get }) => {
    const all = get(actualityJobs);
    const grouped = _.keyBy(all, p => p.jobId) as Record<string, ActualityJob>;
    return grouped;
  },
});

export const actualityJobsTableData = atom<ActualityJobs>({
  key: getKey('jobs-table-data'),
  default: [],
});

export const actualityJobsTableTotalCount = atom<number>({
  key: getKey('jobs-table-total-count'),
  default: 0,
});

export const actualityJobsTableDataLoadStatus = atom<AsyncLoadStatus>({
  key: getKey('jobs-table-data-status'),
  default: AsyncLoadStatus.None,
});

export const actualityJobsTableDataState = atom<
  DataTableState<ActualityJobField>
>({
  key: getKey('jobs-table-data-state'),
  default: {
    sortValues: {
      jobId: SortDirection.DESC,
    },
    searchValues: {},
  },
});

export const actualityJobTimingMode = persistAtom<ActualityJobTimingMode>({
  key: getKey('job-timing-mode'),
  default: 'absolute',
});

// export const actualityAgentsAnalyzedJobs = selector<ActualityAnalyzedJob[]>({
//   key: getKey('agent-jobs'),
//   get: async ({ get }) => {
//     const warehouseId = get(warehouseSelectedId);
//     const actualityBuilderParams = get(actualityQueryBuilderParams);
//     const agentId = get(feedSelectedAgentId);
//     if (_.isNil(agentId)) return [];

//     const params: ActualityAnalyzedJobQueryBuilderPagedParams = {
//       ...actualityBuilderParams,
//       activity: {
//         ...actualityBuilderParams.activity,
//         agentId,
//       },
//       sortBy: {
//         field: 'minEventTime',
//         direction: SortDirection.DESC,
//       },
//     };
//     const compiledQuery = getActualityAnalyzedJobsQuery(params).compile();

//     const [result] = await executeDatasetQuery({
//       warehouseId,
//       compiledQuery,
//       comment: '[feed] Agent analyzed jobs',
//       reuseHashSource: get(actualityQueryReuseHashSources),
//     });
//     return result;
//   },
// });

// export const actualityAgentsAnalyzedJobEvents = selectorFamily<
//   ActualityAnalyzedEvents,
//   string
// >({
//   key: getKey('agents-analyzed-job-events'),
//   get:
//     (jobId: string) =>
//     async ({ get }) => {
//       const warehouseId = get(warehouseSelectedId);
//       const actualityBuilderParams = get(actualityQueryBuilderParams);
//       const agentId = get(feedSelectedAgentId);
//       if (_.isNil(agentId)) return [];

//       const compiledQuery = getActualityAnalyzedEventsQuery({
//         ...actualityBuilderParams,
//         // agentId: agentId,
//         jobId,
//         sortBy: {
//           field: 'eventEndTime',
//           direction: SortDirection.ASC,
//         },
//       }).compile();

//       const [result] = await executeDatasetQuery({
//         warehouseId,
//         compiledQuery,
//         comment: `[feed] Agent analyzed job's events`,
//       });
//       return result;
//     },
// });

// export const actualityAgentsSimulatedJobEvents = selectorFamily<
//   AnalyzeEventFragment[],
//   string
// >({
//   key: getKey('agents-analyzed-job-events'),
//   get:
//     (jobId: string) =>
//     async ({ get }) => {
//       if (_.isNil(jobId)) return [];
//       const warehouseId = get(warehouseSelectedId);
//       const actualityBuilderParams = get(actualityQueryBuilderParams);
//       const agentId = get(feedSelectedAgentId);
//       if (_.isNil(agentId)) return [];
//       const compiledQuery = getActualitySimulatedEventsQuery({
//         ...actualityBuilderParams,
//         agentId: agentId,
//         jobId,
//         sortBy: {
//           field: 'eventDate',
//           direction: SortDirection.ASC,
//         },
//       }).compile();

//       const [result] = await executeDatasetQuery({
//         warehouseId,
//         compiledQuery,
//         comment: `[actuality] Agent simulated job's events`,
//         reuseHashSource: get(actualityQueryReuseHashSources),
//       });
//       return _.map(result, e => {
//         const parsedEvent: AnalyzeEventFragment = {
//           ...e,
//           agents: parseJSON(e.agents),
//           orderIds: [],
//           startPosition: parseJSON(e.startPosition),
//           endPosition: parseJSON(e.endPosition),
//           details: parseJSON(e.details),
//         } as AnalyzeEventFragment;
//         return parsedEvent;
//       });
//     },
// });

// export const actualityJobRoute = selectorFamily<WaypointLabeled[], string>({
//   key: getKey('job-routes'),
//   get:
//     (jobId: string) =>
//     ({ get }) => {
//       const plane = get(viewerSelectedPlane);
//       if (_.isNil(jobId)) return null;
//       const simulatedEvents = get(actualityAgentsSimulatedJobEvents(jobId));

//       return _(simulatedEvents)
//         .filter(
//           event =>
//             (event?.eventType === 'TRAVELLING_HORIZONTAL_START' ||
//               event?.eventType === 'TRAVELLING_HORIZONTAL_END' ||
//               event?.eventType === 'TRAVELLING_HORIZONTAL') &&
//             (event?.startPosition?.planeId === plane.id ||
//               event?.endPosition?.planeId === plane.id),
//         )
//         .map((e, i) => {
//           return getEventRoutePart(jobId, e, plane.id, i === 0);
//         })
//         .flatten()
//         .value();
//     },
// });
